import axios from "axios";
import { getToken } from "../utils/authStorage";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5010/api";

export interface KPIData {
  totalUnits: number;
  availableUnits: number;
  bookedUnits: number;
  soldUnits: number;
  blockedUnits: number;
}

export interface StatusData {
  sold: number;
  unsold: number;
  booked: number;
  hold: number;
  approval_pending: number;
}

export interface UnitTypeData {
  type: string;
  sold: number;
  unsold: number;
  booked: number;
  total: number;
}

export interface WingData {
  wing: string;
  sold: number;
  unsold: number;
  booked: number;
  hold: number;
  total: number;
}

export interface ProjectStatusData {
  projectId: number;
  projectName: string;
  sold: number;
  unsold: number;
  booked: number;
  hold: number;
  approval_pending: number;
}

export interface SalesTimelineData {
  month: string;
  units: number;
}

export interface Project {
  id: number;
  name: string;
}

export interface UnitReportData {
  kpi: KPIData;
  statusData: StatusData;
  unitTypeData: UnitTypeData[];
  wingData: WingData[];
  projectStatusData: ProjectStatusData[];
  salesTimeline: SalesTimelineData[];
  projects: Project[];
}

export interface UnitDetail {
  id: number;
  projectName: string;
  unitNo: string;
  wing: string;
  unitType: string;
  size: string;
  status: string;
  saleDeedAmount: number;
  bookingDate?: string;
  customerName?: string;
}

export interface UnitCellData {
  id: number;
  unitNo: string;
  unitType: string;
  status: string;
  size: string;
  bookingId: string | null;
  customerName: string | null;
}

export interface FloorData {
  floor: number;
  units: UnitCellData[];
}

export interface WingGridData {
  wing: string;
  floors: FloorData[];
}

export interface UnitTypeSummary {
  type: string;
  total: number;
  booked: number;
  unsold: number;
  hold: number;
}

export interface GridStats {
  total: number;
  booked: number;
  unsold: number;
  hold: number;
}

export interface TimelineMonth {
  month: number;
  label: string;
  count: number;
}

export interface TimelineYear {
  year: number;
  count: number;
  months: TimelineMonth[];
}

export interface UnitGridData {
  wings: string[];
  unitTypes: UnitTypeSummary[];
  wingGridData: WingGridData[];
  stats: GridStats;
  bookingTimeline: TimelineYear[];
}

class UnitReportService {
  private getAuthHeader() {
    const token = getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  async getUnitReportData(
    projectId?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<UnitReportData> {
    const params: any = {};
    if (projectId) params.projectId = projectId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    const response = await axios.get(
      `${API_URL}/reports/unit/data`,
      {
        ...this.getAuthHeader(),
        params,
      }
    );
    return response.data.data;
  }

  async getUnitDetails(
    projectId?: string,
    status?: string,
    unitType?: string,
    wing?: string
  ): Promise<UnitDetail[]> {
    const params: any = {};
    if (projectId) params.projectId = projectId;
    if (status) params.status = status;
    if (unitType) params.unitType = unitType;
    if (wing) params.wing = wing;

    const response = await axios.get(
      `${API_URL}/reports/unit/details`,
      {
        ...this.getAuthHeader(),
        params,
      }
    );
    return response.data.data;
  }

  async getUnitGridData(
    projectId: string,
    wings?: string,
    unitType?: string,
    statusFilter?: string,
    year?: string,
    month?: string
  ): Promise<UnitGridData> {
    const params: any = { projectId };
    if (wings) params.wings = wings;
    if (unitType) params.unitType = unitType;
    if (statusFilter && statusFilter !== "all") params.statusFilter = statusFilter;
    if (year) params.year = year;
    if (month) params.month = month;

    const response = await axios.get(
      `${API_URL}/reports/unit/grid-data`,
      {
        ...this.getAuthHeader(),
        params,
      }
    );
    return response.data.data;
  }
}

export default new UnitReportService();
